import _ from 'underscore';
import { useCallback, useContext } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { useInstrumentLogger } from '@bingads-webui-campaign-react/linkedin-instrumentation';
import { useWizardService, getWizardData } from '@bingads-webui-fluent-component/wizard';
import { Campaign, AdGroup } from '@bingads-webui/campaign-odata-schemas';
import {
  readCampaign,
  createOrUpdateCampaign,
  readAdGroup,
  ERROR_ENTITY,
  mapBulkUpsertErrors,
  getBulkUploadErrorType,
  genericApiError,
  parseDateFromAPIFormat,
  formatDateToAPIFormat,
} from '@bingads-webui-campaign-react/linkedin-data';

const bulkUpsertEntityErrorMap = {
  [getBulkUploadErrorType(Campaign)]: ERROR_ENTITY.Campaign,
  [getBulkUploadErrorType(AdGroup)]: ERROR_ENTITY.AdGroup,
};

export const useBudgetRepository = () => {
  const { scope } = useContext(ScopeContext);
  const i18n = useContext(I18nContext);
  const { cid, aid } = scope.values;
  const { state } = useWizardService();
  const {
    campaignId,
    adGroupId,
  } = getWizardData(state);

  const logger = useInstrumentLogger({
    scenarioName: 'LinkedInBudgetStep',
    activityName: 'Repository',
  });

  const load = useCallback(() => {
    const loadCampaign = () => readCampaign(cid, aid, campaignId);
    const loadAdGroup = () => readAdGroup(cid, aid, campaignId, adGroupId);

    const initialRequests = [
      loadCampaign(),
      loadAdGroup(),
    ];

    return Promise.all(initialRequests).then(([
      campaign,
      adGroup,
    ]) => {
      const {
        Budget, BiddingScheme, CampaignSettings = [], TimeZone,
      } = campaign;
      const { Amount } = Budget;
      const { Type: bidStrategy } = BiddingScheme;
      const { StartDate, EndDate, Status } = adGroup;

      return {
        campaignGoal: CampaignSettings[0].Goal,
        budgetAmount: String(Amount),
        bidStrategy,
        StartDate: parseDateFromAPIFormat(i18n, StartDate),
        EndDate: parseDateFromAPIFormat(i18n, EndDate),
        adGroupStatus: Status,
        campaignTimeZone: TimeZone,
      };
    }).catch((error) => {
      logger.trace(`Failed to load budget data: ${JSON.stringify(error)}`);
      throw error;
    });
  }, [adGroupId, aid, campaignId, cid, i18n, logger]);

  const save = useCallback(({ data }) => {
    const {
      budgetAmount,
      bidStrategy,
      StartDate,
      EndDate,
    } = data;

    const modelToSave = {
      Id: campaignId,
      Budget: {
        Amount: budgetAmount,
      },
      BiddingScheme: {
        Type: bidStrategy,
      },
      AdGroups: [{
        Id: adGroupId,
        StartDate: formatDateToAPIFormat(i18n, StartDate),
        EndDate: EndDate ? formatDateToAPIFormat(i18n, EndDate) : '1/1/3000',
        Campaign: { Id: campaignId },
      }],
    };
    logger.trace(`Saving budget data: ${JSON.stringify(modelToSave)}`);

    return createOrUpdateCampaign(modelToSave).then(() => data).catch((error) => {
      logger.trace(`Failed to save budget data: ${JSON.stringify(error)}`);
      const { errors } = error;
      if (!_.isEmpty(errors)) {
        const serverErrors
          = mapBulkUpsertErrors(errors, bulkUpsertEntityErrorMap, _.identity, null, i18n);

        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({ error: { serverErrors } });
      }
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({ error: { serverErrors: [genericApiError] } });
    });
  }, [adGroupId, campaignId, i18n, logger]);

  return {
    load,
    save,
  };
};
